










































































































































































































































































































import {Component, Vue} from 'vue-property-decorator'
import VueMultiselect from 'vue-multiselect/src/Multiselect.vue'
import ContactAttachments from '@/components/contact/ContactAttachments.vue'
import ContactCaptcha from '@/components/contact/ContactCaptcha.vue'
import ContactFormField from '@/components/contact/ContactFormField.vue'
import ContactRepeatableInput from '@/components/contact/ContactRepeatableInput.vue'
import {ContactInquiryType} from '@/enums/ContactInquiryType'
import {ContactRelatedProduct} from '@/enums/ContactRelatedProduct'
import {NetworkNamespace} from '@/enums/NetworkNamespace'
import {ContactRequest} from '@/model/request/ContactRequest'
import {FileHelper} from '@/helpers/FileHelper'

const MAX_SHORT = 70
const MAX_LONG = 320
const MAX_MESSAGE = 1000
const MAX_ENTRIES = 10

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?$/i

interface SelectOption {
  value: string
  label: string
}
type DappField =
  | 'dappTitle'
  | 'dappLink'
  | 'companyName'
  | 'socialLink'
  | 'mainnetContract'
  | 'tokens'

type ContactField =
  | 'inquiryType'
  | 'name'
  | 'email'
  | 'relatedProduct'
  | 'message'
  | 'captcha'
  | DappField

const DAPP_FIELDS: DappField[] = [
  'dappTitle',
  'dappLink',
  'companyName',
  'socialLink',
  'mainnetContract',
  'tokens',
]

@Component({
  components: {
    ContactAttachments,
    ContactCaptcha,
    ContactFormField,
    ContactRepeatableInput,
    VueMultiselect,
  },
})
export default class ContactForm extends Vue {
  readonly maxShort = MAX_SHORT
  readonly maxLong = MAX_LONG
  readonly maxMessage = MAX_MESSAGE
  readonly maxEntries = MAX_ENTRIES

  inquiryType: SelectOption | null = null
  relatedProduct: SelectOption | null = null

  network: SelectOption[] = []

  name = ''
  email = ''
  message = ''
  dappTitle = ''
  dappLink = ''
  companyName = ''

  socialLink: string[] = ['']
  mainnetContract: string[] = ['']
  tokens: string[] = ['']

  attachments: File[] = []

  captchaToken: string | null = null

  isCaptchaUnavailable = false

  touched: Partial<Record<ContactField, boolean>> = {}
  submitAttempted = false
  isSubmitted = false

  get inquiryTypeOptions(): SelectOption[] {
    return Object.values(ContactInquiryType).map(value => ({
      value,
      label: this.$translate(
        `components.contactForm.inquiryType.${value}`
      ) as string,
    }))
  }

  get relatedProductOptions(): SelectOption[] {
    return Object.values(ContactRelatedProduct).map(value => ({
      value,
      label: this.$translate(
        `components.contactForm.relatedProduct.${value}`
      ) as string,
    }))
  }

  get networkOptions(): SelectOption[] {
    return Object.values(NetworkNamespace).map(value => ({
      value,
      label: this.$translate(
        `components.contactForm.network.${value}`
      ) as string,
    }))
  }

  get linkPlaceholder() {
    return this.$translate('components.contactForm.linkPlaceholder') as string
  }

  get isSubmitDapp() {
    return this.inquiryType?.value === ContactInquiryType.SUBMIT_DAPP
  }

  get visibleFields(): ContactField[] {
    const fields: ContactField[] = ['inquiryType', 'name', 'email']

    if (!this.inquiryType) {
      return fields
    }

    if (this.isSubmitDapp) {
      fields.push(...DAPP_FIELDS)
    } else {
      fields.push('relatedProduct')
    }

    return [...fields, 'message', 'captcha']
  }

  get fieldErrors(): Partial<Record<ContactField, string>> {
    return this.visibleFields.reduce((acc, field) => {
      const message = this.validate(field)

      if (message) {
        acc[field] = message
      }

      return acc
    }, {} as Partial<Record<ContactField, string>>)
  }

  get isValid() {
    return !Object.keys(this.fieldErrors).length
  }

  get pendingFieldLabels(): string[] {
    return Object.keys(this.fieldErrors).map(field => {
      const key = field === 'captcha' ? 'verification' : field
      return this.$translate(`components.contactForm.label.${key}`) as string
    })
  }

  isFieldVisible(field: ContactField) {
    return this.visibleFields.includes(field)
  }

  errorOf(field: ContactField): string | null {
    const shouldShow = this.submitAttempted || this.touched[field]
    return shouldShow ? this.fieldErrors[field] ?? null : null
  }

  touch(field: ContactField) {
    this.$set(this.touched, field, true)
  }

  validate(field: ContactField): string | null {
    switch (field) {
      case 'inquiryType':
        return this.inquiryType ? null : this.requiredMessage
      case 'relatedProduct':
        return this.relatedProduct ? null : this.requiredMessage
      case 'captcha':
        return this.captchaToken ? null : this.requiredMessage
      case 'name':
        return this.required(this.name) ?? this.maxLength(this.name, MAX_SHORT)
      case 'email':
        return (
          this.required(this.email) ??
          this.maxLength(this.email, MAX_LONG) ??
          this.emailFormat(this.email)
        )
      case 'message':
        return (
          this.required(this.message) ??
          this.maxLength(this.message, MAX_MESSAGE)
        )
      case 'dappTitle':
        return (
          this.required(this.dappTitle) ??
          this.maxLength(this.dappTitle, MAX_SHORT)
        )
      case 'dappLink':
        return (
          this.required(this.dappLink) ??
          this.maxLength(this.dappLink, MAX_SHORT) ??
          this.urlFormat(this.dappLink)
        )
      case 'companyName':
        return (
          this.required(this.companyName) ??
          this.maxLength(this.companyName, MAX_SHORT)
        )
      case 'socialLink':
        return this.optionalList(
          this.socialLink,
          value => this.maxLength(value, MAX_SHORT) ?? this.urlFormat(value)
        )
      case 'mainnetContract':
        return this.optionalList(this.mainnetContract, value =>
          this.maxLength(value, MAX_LONG)
        )
      case 'tokens':
        return this.optionalList(this.tokens, value =>
          this.maxLength(value, MAX_LONG)
        )
      default:
        return null
    }
  }

  async submit() {
    this.submitAttempted = true

    if (Object.keys(this.fieldErrors).length) {
      this.$toast.abort('system.error.validation')
      return
    }

    try {
      await this.$await.run('submitContact', async () =>
        (await this.buildRequest()).submit()
      )
    } catch (error) {
      this.resetCaptcha()
      throw error
    }

    this.isSubmitted = true
    this.$emit('submit')
  }

  async buildRequest(): Promise<ContactRequest> {
    const request = new ContactRequest()

    request.attachments = await Promise.all(
      this.attachments.map(async file => ({
        filename: file.name,
        content: await FileHelper.fileToBase64(file),
      }))
    )

    request.inquiryType = this.inquiryType?.value ?? null
    request.name = this.name.trim()
    request.email = this.email.trim()
    request.message = this.message.trim()
    request.recaptchaToken = this.captchaToken

    if (this.isSubmitDapp) {
      request.dappTitle = this.dappTitle.trim()
      request.dappLink = this.dappLink.trim()
      request.companyName = this.companyName.trim()
      request.socialLink = this.cleanEntries(this.socialLink)
      request.network = this.network.map(option => option.value)
      request.mainnetContract = this.cleanEntries(this.mainnetContract)
      request.tokens = this.cleanEntries(this.tokens)
    } else {
      request.relatedProduct = this.relatedProduct?.value ?? null
    }

    return request
  }

  resetCaptcha() {
    this.captchaToken = null
    ;(this.$refs.captcha as ContactCaptcha | undefined)?.reset()
  }

  handleInquiryTypeChange() {
    this.relatedProduct = null
    this.network = []

    this.dappTitle = ''
    this.dappLink = ''
    this.companyName = ''
    this.socialLink = ['']
    this.mainnetContract = ['']
    this.tokens = ['']

    DAPP_FIELDS.forEach(field => this.$delete(this.touched, field))
  }

  private cleanEntries(values: string[]): string[] {
    return values
      .map(value => value.trim())
      .filter(Boolean)
      .slice(0, MAX_ENTRIES)
  }

  private get requiredMessage() {
    return this.$translate('components.contactForm.error.required') as string
  }

  private required(value: string): string | null {
    return value.trim() ? null : this.requiredMessage
  }

  private optionalList(
    values: string[],
    // eslint-disable-next-line no-unused-vars
    check: (value: string) => string | null
  ): string | null {
    const entries = values.map(value => value.trim()).filter(Boolean)

    if (entries.length > MAX_ENTRIES) {
      return this.$translate('components.contactForm.error.maxEntries', {
        max: MAX_ENTRIES,
      }) as string
    }

    for (const entry of entries) {
      const error = check(entry)
      if (error) {
        return error
      }
    }

    return null
  }

  private maxLength(value: string, max: number): string | null {
    return value.length > max
      ? (this.$translate('components.contactForm.error.maxLength', {
          max,
        }) as string)
      : null
  }

  private emailFormat(value: string): string | null {
    return EMAIL_REGEX.test(value.trim())
      ? null
      : (this.$translate('components.contactForm.error.email') as string)
  }

  private urlFormat(value: string): string | null {
    return URL_REGEX.test(value.trim())
      ? null
      : (this.$translate('components.contactForm.error.url') as string)
  }
}
